import type { FC } from "react";

import { type OrderAttribute } from "@jti/magento";
import { FormField, TextInput, Txt, useTranslations } from "@jti/ui";

import { FormFields } from "@checkout/constants/checkout-constants";

import styles from "./OrderAttributeTextInput.module.scss";

type OrderAttributeProps = {
  orderAttribute: OrderAttribute;
};

const createRegExpFromString = (str: string): RegExp | null => {
  try {
    return new RegExp(str);
  } catch {
    console.error("no valid RegExp for ", str);
    return null;
  }
};

export const OrderAttributeTextInput: FC<OrderAttributeProps> = ({ orderAttribute }) => {
  const { __ } = useTranslations();

  const fieldRegex = createRegExpFromString(orderAttribute.validate_regex);

  const patternRules =
    orderAttribute.validate_regex && fieldRegex
      ? ({
          pattern: {
            value: fieldRegex,
            message: __("form_validation.validate_regex"),
          },
          valueAsNumber: false,
        } as const)
      : ({
          valueAsNumber: orderAttribute.numeric,
        } as const);

  return (
    <div className={styles.orderAttribute} data-testid="order-attribute-text-input">
      <FormField
        id={orderAttribute.code}
        label={orderAttribute.label}
        name={`${FormFields.OrderAttributes}.${orderAttribute.code}`}
        defaultValue={orderAttribute.value || ""}
        className={styles.field}
        showCustomError
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            id={orderAttribute.code}
            data-testid={`${FormFields.OrderAttributes}-${orderAttribute.code}`}
            placeholder={orderAttribute.placeholder || ""}
            autoComplete="off"
            isError={fieldState.invalid}
            errorText={fieldState.error?.message || ""}
          />
        )}
        validation={{
          ...(orderAttribute.is_required && {
            required: {
              value: orderAttribute.is_required,
              message: __("form_validation.field_required"),
            },
          }),
          ...(orderAttribute.min_length && {
            minLength: {
              value: orderAttribute.min_length,
              message: __("form_validation.min_length", [orderAttribute.min_length]),
            },
          }),
          ...(orderAttribute.max_length && {
            maxLength: {
              value: orderAttribute.max_length,
              message: __("form_validation.max_length", [orderAttribute.max_length]),
            },
          }),
          ...patternRules,
        }}
      />
      {orderAttribute.description && (
        <Txt variant={"small-text"} dataTestId={`${orderAttribute.code}-description`} as={"span"}>
          {orderAttribute.description}
        </Txt>
      )}
    </div>
  );
};
